import React, { useEffect, useRef } from "react"
import { SSEWrapper } from "./SSEWrapper"
import { PaymentLoader } from "../PaymentLoader"
import { CheckoutOrder } from "../../../models/order"
import { AdyenCheckout, Swish } from "@adyen/adyen-web"
import {
  getAdyenCountryCode,
  getAdyenEnvironment,
  getAdyenLocale,
} from "../../../util/util"
import { useI18next } from "gatsby-plugin-react-i18next"
import { getAdyenSwishAction } from "../../../services/adyen-swish-service"
import { AdyenSwishWalletQRScanner } from "./AdyenSwishWalletQRScanner"

type Props = {
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
  showPaymentSucceededScreen: () => void
  order: CheckoutOrder
  countryCode: string
  slug: string
}

export const AdyenSwishWalletQRScannerSSEWrapper = ({
  showPaymentFailedScreen,
  showPaymentSucceededScreen,
  order,
  countryCode,
  slug,
}: Props) => {
  const SwishRef = useRef<HTMLDivElement>(null)
  const swishComponentRef = useRef<Swish | null>(null)

  const { language } = useI18next()

  useEffect(() => {
    const mountSwish = !!SwishRef.current && !swishComponentRef.current

    if (!mountSwish) {
      return
    }

    const init = async () => {
      const storedAction = getAdyenSwishAction()

      if (!storedAction) {
        showPaymentFailedScreen(
          "Something went wrong when trying to render the Swish QR code scanner"
        )
        return
      }

      const checkout = await AdyenCheckout({
        clientKey: storedAction.clientKey,
        environment: getAdyenEnvironment(storedAction.clientKey),
        locale: getAdyenLocale(language),
        countryCode: getAdyenCountryCode(countryCode),
      })

      checkout.createFromAction(storedAction.action).mount(SwishRef.current!)
    }

    init()
  }, [countryCode, language, slug, showPaymentFailedScreen])

  return (
    <SSEWrapper
      showPaymentFailedScreen={showPaymentFailedScreen}
      showPaymentSucceededScreen={showPaymentSucceededScreen}
      order={order}
      renderChild={({
        paymentProcessing,
        sseSubscriber,
        setPaymentProcessing,
      }) => (
        <>
          {paymentProcessing ? (
            <PaymentLoader />
          ) : (
            <AdyenSwishWalletQRScanner
              sseSubscriber={sseSubscriber}
              showPaymentFailedScreen={showPaymentFailedScreen}
              showPaymentSucceededScreen={showPaymentSucceededScreen}
              order={order}
              countryCode={countryCode}
              slug={slug}
              paymentProcessing={paymentProcessing}
              setPaymentProcessing={setPaymentProcessing}
            />
          )}
        </>
      )}
    />
  )
}
